import React, { useEffect, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import  { Button, Spinner, Form, InputGroup, FormControl } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import ListTable from '../../components/ListTable';
import EditRegionModal from '../../components/EditRegionModal';
import Container from '../../layouts/Container';
import { getRegions } from '../../services/project/projectService';
import { createRegion, deleteRegion, exportRegion, importRegion } from '../../services/project/regionService';
import InputWithButton from '../../components/InputWithButton';
import { Pencil, XOctagon, Upload  } from 'react-bootstrap-icons';
import AnchorTag from '../../components/AnchorTag';
import { useProjects } from '../../context/ProjectContext';

function Regions() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [newRegionName, setNewRegionName] = useState('');
  const [newRegionSortOrder, setNewRegionSortOrder] = useState('');

  const { projectId } = useParams();
  const { isProjectReadOnly } = useProjects();

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportRegion(projectId);
    } catch (error) {
      console.error(error);
      alert('Failed to download export');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) return toast.error('Please select a JSON file');
    try {
      setIsImporting(true);
      const result = await importRegion(projectId, file);
      toast.success(result.message || 'Import completed');
      setFile(null);
      await fetchRegions();
    } catch (err) {
      console.error(err);
      toast.error('Import failed');
    } finally {
      setIsImporting(false);
    }
  };
  
  const handleEditRegion = (region) => {
    setSelectedRegion(region);
    setShowEditModal(true);
  };
  
  const handleDeleteRegion = async (projectId, regionId) => {
    const confirmed = window.confirm('Are you sure?');
    if (!confirmed) return;
    try {
      const response = await deleteRegion(projectId, regionId);
    
      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      fetchRegions();
      toast.success(response.message);
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleCreateRegion = async (projectId, regionData) => {
    try {
      const response = await createRegion(projectId, regionData);
      if (!response.success) {
        toast.error(response.data.error);
        return;
      }
      fetchRegions();
      toast.success(response.message);
      // Clear form
      setNewRegionName('');
      setNewRegionSortOrder('');
    } catch (error) {
      console.error('Error creating region:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  }
  
  const fetchRegions = async () => {
    try {
      const response = await getRegions(projectId);
      if (!response.success) {
        toast.error(response.data?.errorMessage || 'Error fetching regions.');
        return;
      }
      setRegions(response.regions);
      if (showEditModal) setSelectedRegion(response.regions.find(r => r.id === selectedRegion.id))
    } catch (error) {
      console.error('Error fetching regions:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  useEffect(() => {
    fetchRegions();
    setLoading(false);
  }, []);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor(
      row => row.id,
      {
        id: 'id',
        header: 'ID',
        cell: info => info.getValue(),
      }
    ),
    columnHelper.accessor(
      row => row.name,
      {
        id: 'regionName',
        header: 'Region Name',
        cell: info => {
          const region = info.row.original;
          return (
            <Link to={`/projects/${projectId}/regions/${region.id}/areas`}>
              {region.name}
            </Link>
          );
        },
      }
    ),

    columnHelper.accessor(
      row => row.provinces.map(p => p.name).join(', '),
      {
        id: 'provinces',
        header: 'Provinces',
        cell: info => info.getValue() || '-',
      }
    ),

    columnHelper.accessor(
      row => row.sortOrder,
      {
        id: 'sortOrder',
        header: 'Sort Order',
        cell: info => info.getValue() ?? '-',
      }
    ),

    (isProjectReadOnly ? false : columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: info => {
        const region = info.row.original;
        return (
          <div className="d-flex gap-2">
            <AnchorTag onClick={() => handleEditRegion(region)} hoverText='Edit'>
              <Pencil />
            </AnchorTag>
            <AnchorTag onClick={() => handleDeleteRegion(projectId, region.id)} hoverText='Delete'>
              <XOctagon />
            </AnchorTag>
          </div>
        );
      },
    })),
  ].filter(Boolean);


  return (
    <Container>
      <h2 style={{ marginBottom: '1rem' }}>Region List</h2>
      {!isProjectReadOnly && 
      (<><Form className="mb-3" onSubmit={(e) => { 
        e.preventDefault(); 
        if (!newRegionName.trim()) {
          toast.error('Region name is required');
          return;
        }
        handleCreateRegion(projectId, { 
          name: newRegionName.trim(), 
          sortOrder: newRegionSortOrder ? parseInt(newRegionSortOrder) : null,
          provinceIds: [] 
        });
      }}>
        <InputGroup>
          <FormControl
            placeholder="Enter region name"
            value={newRegionName}
            onChange={(e) => setNewRegionName(e.target.value)}
            required
          />
          <FormControl
            type="number"
            placeholder="Sort order (optional)"
            value={newRegionSortOrder}
            onChange={(e) => setNewRegionSortOrder(e.target.value)}
          />
          <Button variant="primary" type="submit">
            Add New
          </Button>
        </InputGroup>
      </Form>
      <Form onSubmit={(e) => { e.preventDefault(); handleImport(); }}>
        <InputGroup>
          <Form.Control
            type="file"
              accept="application/json"
              onChange={(e) => setFile(e.target.files[0] || null)}
              aria-label="Import Region Config JSON"
            />
            <Button
              variant="info"
              type="submit"
              disabled={isImporting || !file}
            >
              {isImporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Importing…
                </>
              ) : (
                <>
                  <Upload className="me-2" />
                  Import
                </>
              )}
            </Button>
            <Button variant="success" disabled={isExporting} onClick={handleExport}>
              {isExporting ? <Spinner size="sm" /> : 'Export'}
            </Button>
          </InputGroup>
          {file && (
            <div className="form-text text-truncate mt-1">
              Selected: {file.name}
            </div>
          )}
        </Form>
        <br></br>
        <br></br></>)}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <ListTable tableId='region-list' data={regions} columns={columns} />
          { (showEditModal && selectedRegion) && (
            <EditRegionModal
              projectId={projectId}
              region={selectedRegion}
              show={showEditModal}
              onRegionMutation={fetchRegions}
              onClose={() => setShowEditModal(false)}
            />
          )}

        </>

      )}
    </Container>
  );
}

export default Regions;
